/**
 * Admin Clips List Module
 * 
 * Displays clips in a table with state badges, filters, and actions.
 * Respects RBAC permissions for showing/hiding actions.
 */

import logger from '../../core/logger.js';
import { adminClipsService } from '../../services/admin-clips-service.js';
import { can } from '../../utils/rbac-helpers.js';
import { Permission, ClipState } from '../../types/rbac.js';
import { AdminClip, ClipsFilters } from '../../types/admin-clip.js';
import { createStateBadge, getStateDescription } from '../../ui/admin/state-badge.js';
import { showAudioGenerationDialog } from '../../ui/admin/audio-generation-dialog.js';
import toast from '../../ui/toast.js';

/**
 * Clips list callbacks
 */
interface ClipsListCallbacks {
  onEditClip: (clipId: string) => void;
  onCreateClip: () => void;
}

let callbacks: ClipsListCallbacks | null = null;

/**
 * Register callbacks
 */
export function registerClipsListCallbacks(cbs: ClipsListCallbacks): void {
  callbacks = cbs;
}

/**
 * Current filters
 */
let currentFilters: ClipsFilters = {};
let currentPage = 1;
const perPage = 20;

/**
 * Render the clips list view
 */
export async function renderClipsListView(container: HTMLElement): Promise<void> {
  logger.info('admin: Rendering clips list');
  
  // Clear container
  container.innerHTML = '';
  
  // Check permissions
  if (!can(Permission.VIEW_JOBS)) {
    renderAccessDenied(container);
    return;
  }
  
  // Create header
  const header = document.createElement('div');
  header.className = 'admin-header';
  
  const heading = document.createElement('h1');
  heading.className = 'admin-heading';
  heading.textContent = 'Clips Management';
  header.appendChild(heading);
  
  const subheading = document.createElement('p');
  subheading.className = 'admin-subheading';
  subheading.textContent = 'Manage clip lifecycle, validation, and publishing';
  header.appendChild(subheading);
  
  container.appendChild(header);
  
  // Create action bar
  if (can(Permission.CREATE_CLIP)) {
    const actionBar = document.createElement('div');
    actionBar.style.marginBottom = '1.5rem';
    
    const createBtn = document.createElement('button');
    createBtn.className = 'btn btn--primary';
    createBtn.textContent = '+ Create New Clip';
    createBtn.addEventListener('click', () => {
      if (callbacks) callbacks.onCreateClip();
    });
    actionBar.appendChild(createBtn);
    
    container.appendChild(actionBar);
  }
  
  // Create clips list section
  const listSection = document.createElement('div');
  listSection.className = 'clips-list';
  
  // Filters
  const filtersDiv = renderFilters();
  listSection.appendChild(filtersDiv);
  
  // Table container (we'll load clips into this)
  const tableContainer = document.createElement('div');
  tableContainer.id = 'clips-table-container';
  listSection.appendChild(tableContainer);
  
  container.appendChild(listSection);
  
  // Load clips
  await loadAndRenderClips(tableContainer);
}

/**
 * Render filters section
 */
function renderFilters(): HTMLElement {
  const filtersDiv = document.createElement('div');
  filtersDiv.className = 'clips-filters';
  
  // State filter
  const stateFilter = createFilterSelect(
    'State',
    'state',
    [
      { value: '', label: 'All States' },
      { value: ClipState.Draft, label: 'Draft' },
      { value: ClipState.TextReview, label: 'Text Review' },
      { value: ClipState.TextApproved, label: 'Text Approved' },
      { value: ClipState.AudioQueued, label: 'Audio Queued' },
      { value: ClipState.AudioReady, label: 'Audio Ready' },
      { value: ClipState.AudioReview, label: 'Audio Review' },
      { value: ClipState.AudioApproved, label: 'Audio Approved' },
      { value: ClipState.Published, label: 'Published' }
    ],
    (value) => {
      currentFilters.state = value ? (value as ClipState) : undefined;
      currentPage = 1;
      const tableContainer = document.getElementById('clips-table-container');
      if (tableContainer) loadAndRenderClips(tableContainer);
    }
  );
  filtersDiv.appendChild(stateFilter);
  
  // Language filter
  const languageFilter = createFilterSelect(
    'Language',
    'language',
    [
      { value: '', label: 'All Languages' },
      { value: 'tr', label: 'Turkish' },
      { value: 'de', label: 'German' },
      { value: 'en', label: 'English' }
    ],
    (value) => {
      currentFilters.language = value ? (value as 'tr' | 'de' | 'en') : undefined;
      currentPage = 1;
      const tableContainer = document.getElementById('clips-table-container');
      if (tableContainer) loadAndRenderClips(tableContainer);
    }
  );
  filtersDiv.appendChild(languageFilter);
  
  // Level filter
  const levelFilter = createFilterSelect(
    'Level',
    'level',
    [
      { value: '', label: 'All Levels' },
      { value: 'B2', label: 'B2' },
      { value: 'B1', label: 'B1' }
    ],
    (value) => {
      currentFilters.level = value ? (value as 'B2' | 'B1') : undefined;
      currentPage = 1;
      const tableContainer = document.getElementById('clips-table-container');
      if (tableContainer) loadAndRenderClips(tableContainer);
    }
  );
  filtersDiv.appendChild(levelFilter);
  
  return filtersDiv;
}

/**
 * Create a filter select element
 */
function createFilterSelect(
  label: string,
  name: string,
  options: Array<{ value: string; label: string }>,
  onChange: (value: string) => void
): HTMLElement {
  const group = document.createElement('div');
  group.className = 'filter-group';
  
  const labelEl = document.createElement('label');
  labelEl.className = 'filter-label';
  labelEl.textContent = label;
  labelEl.htmlFor = `filter-${name}`;
  group.appendChild(labelEl);
  
  const select = document.createElement('select');
  select.id = `filter-${name}`;
  select.className = 'filter-select';
  
  options.forEach(opt => {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    select.appendChild(option);
  });
  
  select.addEventListener('change', () => onChange(select.value));
  
  group.appendChild(select);
  return group;
}

/**
 * Load and render clips table
 */
async function loadAndRenderClips(container: HTMLElement): Promise<void> {
  try {
    // Show loading state
    container.innerHTML = '<div style="padding: 2rem; text-align: center;">Loading clips...</div>';
    
    // Fetch clips
    const response = await adminClipsService.listClips(currentFilters, currentPage, perPage);
    
    // Clear container
    container.innerHTML = '';
    
    if (response.clips.length === 0) {
      container.innerHTML = '<div style="padding: 2rem; text-align: center; color: #6b7280;">No clips found</div>';
      return;
    }
    
    // Create table
    const table = document.createElement('table');
    table.className = 'clips-table';
    
    // Table header
    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr>
        <th>Title</th>
        <th>Language</th>
        <th>Level</th>
        <th>State</th>
        <th>Updated</th>
        <th>Actions</th>
      </tr>
    `;
    table.appendChild(thead);
    
    // Table body
    const tbody = document.createElement('tbody');
    response.clips.forEach(clip => {
      const row = createClipRow(clip);
      tbody.appendChild(row);
    });
    table.appendChild(tbody);
    
    container.appendChild(table);
    
    // Pagination
    if (response.hasMore || currentPage > 1) {
      const pagination = createPagination(response);
      container.appendChild(pagination);
    }
    
  } catch (error) {
    logger.error('admin: Failed to load clips', { error });
    container.innerHTML = '<div style="padding: 2rem; text-align: center; color: #dc2626;">Failed to load clips</div>';
  }
}

/**
 * Create a table row for a clip
 */
function createClipRow(clip: AdminClip): HTMLTableRowElement {
  const row = document.createElement('tr');
  
  // Title
  const titleCell = document.createElement('td');
  titleCell.className = 'clip-title-cell';
  titleCell.textContent = clip.title;
  row.appendChild(titleCell);
  
  // Language
  const langCell = document.createElement('td');
  langCell.textContent = clip.language.toUpperCase();
  row.appendChild(langCell);
  
  // Level
  const levelCell = document.createElement('td');
  levelCell.textContent = clip.level;
  row.appendChild(levelCell);
  
  // State
  const stateCell = document.createElement('td');
  const badge = createStateBadge(clip.state);
  badge.title = getStateDescription(clip.state);
  stateCell.appendChild(badge);
  row.appendChild(stateCell);
  
  // Updated
  const updatedCell = document.createElement('td');
  const date = new Date(clip.updatedAt);
  updatedCell.textContent = date.toLocaleDateString();
  row.appendChild(updatedCell);
  
  // Actions
  const actionsCell = document.createElement('td');
  actionsCell.className = 'clip-actions';
  
  // View/Edit button
  const viewBtn = document.createElement('button');
  viewBtn.className = 'action-btn';
  viewBtn.textContent = 'View';
  viewBtn.addEventListener('click', () => {
    if (callbacks) callbacks.onEditClip(clip.id);
  });
  actionsCell.appendChild(viewBtn);
  
  // State-specific quick actions
  if (clip.state === ClipState.Draft && can(Permission.SUBMIT_FOR_REVIEW)) {
    const submitBtn = document.createElement('button');
    submitBtn.className = 'action-btn action-btn--primary';
    submitBtn.textContent = 'Submit for Review';
    submitBtn.addEventListener('click', async () => {
      await handleSubmitForReview(clip.id);
    });
    actionsCell.appendChild(submitBtn);
  }
  
  if (clip.state === ClipState.TextReview && can(Permission.APPROVE_TEXT)) {
    const approveBtn = document.createElement('button');
    approveBtn.className = 'action-btn action-btn--primary';
    approveBtn.textContent = 'Approve Text';
    approveBtn.addEventListener('click', async () => {
      await handleApproveText(clip.id);
    });
    actionsCell.appendChild(approveBtn);
  }
  
  if (clip.state === ClipState.TextApproved && can(Permission.TRIGGER_AUDIO_GENERATION)) {
    const audioBtn = document.createElement('button');
    audioBtn.className = 'action-btn action-btn--primary';
    audioBtn.textContent = 'Generate Audio';
    audioBtn.addEventListener('click', async () => {
      await handleGenerateAudio(clip.id);
    });
    actionsCell.appendChild(audioBtn);
  }
  
  if (clip.state === ClipState.AudioReview && can(Permission.APPROVE_AUDIO)) {
    const approveBtn = document.createElement('button');
    approveBtn.className = 'action-btn action-btn--primary';
    approveBtn.textContent = 'Approve Audio';
    approveBtn.addEventListener('click', async () => {
      await handleApproveAudio(clip.id);
    });
    actionsCell.appendChild(approveBtn);
  }
  
  if (clip.state === ClipState.AudioApproved && can(Permission.PUBLISH_CLIP)) {
    const publishBtn = document.createElement('button');
    publishBtn.className = 'action-btn action-btn--primary';
    publishBtn.textContent = 'Publish';
    publishBtn.addEventListener('click', async () => {
      await handlePublish(clip.id);
    });
    actionsCell.appendChild(publishBtn);
  }
  
  row.appendChild(actionsCell);
  
  return row;
}

/**
 * Create pagination controls
 */
function createPagination(response: { hasMore: boolean; page: number }): HTMLElement {
  const pagination = document.createElement('div');
  pagination.className = 'pagination-container';
  
  if (currentPage > 1) {
    const prevBtn = document.createElement('button');
    prevBtn.className = 'action-btn';
    prevBtn.textContent = '← Previous';
    prevBtn.addEventListener('click', () => {
      currentPage--;
      const tableContainer = document.getElementById('clips-table-container');
      if (tableContainer) loadAndRenderClips(tableContainer);
    });
    pagination.appendChild(prevBtn);
  }
  
  const pageInfo = document.createElement('span');
  pageInfo.className = 'pagination-info';
  pageInfo.textContent = `Page ${currentPage}`;
  pagination.appendChild(pageInfo);
  
  if (response.hasMore) {
    const nextBtn = document.createElement('button');
    nextBtn.className = 'action-btn';
    nextBtn.textContent = 'Next →';
    nextBtn.addEventListener('click', () => {
      currentPage++;
      const tableContainer = document.getElementById('clips-table-container');
      if (tableContainer) loadAndRenderClips(tableContainer);
    });
    pagination.appendChild(nextBtn);
  }
  
  return pagination;
}

/**
 * Quick action handlers
 */
async function handleSubmitForReview(clipId: string): Promise<void> {
  try {
    await adminClipsService.submitForReview(clipId);
    // Reload table
    const tableContainer = document.getElementById('clips-table-container');
    if (tableContainer) await loadAndRenderClips(tableContainer);
    toast.success('Clip submitted for review');
  } catch (error) {
    logger.error('admin: Failed to submit for review', { clipId, error });
    toast.error('Failed to submit clip for review');
  }
}

async function handleApproveText(clipId: string): Promise<void> {
  try {
    await adminClipsService.approveText(clipId);
    const tableContainer = document.getElementById('clips-table-container');
    if (tableContainer) await loadAndRenderClips(tableContainer);
    toast.success('Text approved');
  } catch (error) {
    logger.error('admin: Failed to approve text', { clipId, error });
    toast.error('Failed to approve text');
  }
}

async function handleGenerateAudio(clipId: string): Promise<void> {
  try {
    // Fetch the clip to get format and speakers
    const clip = await adminClipsService.getClip(clipId);
    
    // Show audio generation dialog with voice selection
    showAudioGenerationDialog(clip, {
      onJobQueued: async (jobId: string) => {
        logger.info('admin: Audio generation job queued', { clipId, jobId });
        const tableContainer = document.getElementById('clips-table-container');
        if (tableContainer) await loadAndRenderClips(tableContainer);
      },
      onCancel: () => {
        logger.info('admin: Audio generation cancelled', { clipId });
      }
    });
  } catch (error) {
    logger.error('admin: Failed to fetch clip or open audio generation dialog', { clipId, error });
    toast.error('Failed to load clip data for audio generation');
  }
}

async function handleApproveAudio(clipId: string): Promise<void> {
  try {
    await adminClipsService.approveAudio(clipId);
    const tableContainer = document.getElementById('clips-table-container');
    if (tableContainer) await loadAndRenderClips(tableContainer);
    toast.success('Audio approved');
  } catch (error) {
    logger.error('admin: Failed to approve audio', { clipId, error });
    toast.error('Failed to approve audio');
  }
}

async function handlePublish(clipId: string): Promise<void> {
  if (!confirm('Are you sure you want to publish this clip?')) {
    return;
  }
  
  try {
    await adminClipsService.publishClip(clipId);
    const tableContainer = document.getElementById('clips-table-container');
    if (tableContainer) await loadAndRenderClips(tableContainer);
    toast.success('Clip published successfully!');
  } catch (error) {
    logger.error('admin: Failed to publish clip', { clipId, error });
    toast.error('Failed to publish clip');
  }
}

/**
 * Render access denied message
 */
function renderAccessDenied(container: HTMLElement): void {
  container.innerHTML = `
    <div class="access-denied-container">
      <h2 class="access-denied-heading">Access Denied</h2>
      <p class="access-denied-message">You do not have permission to access the admin panel.</p>
    </div>
  `;
}
